import { styled, Theme, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import DrawIcon from "@mui/icons-material/Draw";
import EditOffIcon from "@mui/icons-material/EditOff";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { useGetEntityMemberPageQuery } from "_shared/entity-members/_api/entity-member-api";
import EntityMemberQuery, {
  ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH,
  EntityMemberOrderField,
} from "_shared/entity-members/_api/queries/entity-member-query";
import useMemberSigningAuthority, {
  type MemberSigningAuthority,
} from "_shared/entity-members/_views/use-member-signing-authority";
import EntityMemberSummary from "_shared/entity-members/entity-member-summary";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { memberRole } from "permissions/_views/wording/permission-wording";
import { personPermissionsPath } from "permissions/_views/people/person-permissions-path";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import {
  createMRTColumnHelper,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_Updater,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
} from "material-react-table";
import {
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
} from "_shared/_views/tables/table-toolbar";

import { ToolbarButtons, ToolbarButtonsContainer } from "_shared/_views/tables/toolbar-buttons";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import * as React from "react";
import { ReactNode, useCallback } from "react";
import { useNavigate } from "react-router";
import useSessionUser from "permissions/session/use-session-user";
import { useSession } from "users/__session/session-context";
import { DateCell } from "_shared/_views/tables/date-cells";
import { formatDate } from "_shared/formatting/dates";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";

const NameCellContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  minWidth: 0,
}));

/** The sortable columns, by the ids the sort state carries. */
export const NAME_COLUMN = "name";
export const DATE_JOINED_COLUMN = "dateJoined";
/** The Email column's id. It folds under the name when the pane is short of room (#6722). */
export const EMAIL_COLUMN = "email";
/** The Role column's id. */
export const ROLE_COLUMN = "isAdmin";

/** What keys a row that is a loading placeholder rather than a member. */
export const SKELETON_ROW_PREFIX = "skeleton-";

/** Sorted by name, ascending: the order a roster is read in, and the server's own default. */
export const DEFAULT_SORTING: MRT_SortingState = [{ id: NAME_COLUMN, desc: false }];

type EntityMembersTableInputProps = {
  canManageMemberAccess: boolean;
  /** The page's way to invite people, drawn in the wide layout's toolbar. Absent when it has none. */
  inviteAction?: ReactNode;
  renderTopToolbarCustomActions: (props: { table: MRT_TableInstance<EntityMemberSummary> }) => ReactNode;
};

const membersColumnHelper = createMRTColumnHelper<EntityMemberSummary>();

/**
 * The order the server is asked for, from the order the table shows. Always one column and one
 * direction: the endpoint sorts every answer, so there is no unsorted state to offer.
 */
export function entityMemberOrderFrom(sorting: MRT_SortingState): {
  orderBy: EntityMemberOrderField;
  orderDir: OrderDirection;
} {
  const first = sorting.length > 0 ? sorting[0] : undefined;
  return {
    orderBy: first && first.id === DATE_JOINED_COLUMN ? EntityMemberOrderField.DateJoined : EntityMemberOrderField.Name,
    orderDir: first && first.desc ? OrderDirection.Descending : OrderDirection.Ascending,
  };
}

/**
 * The key MRT files each row under. While nothing has arrived yet MRT keys blank placeholder rows
 * built from the column ids, with no id on them, so a placeholder is keyed by its position.
 */
export function entityMemberRowId(row: EntityMemberSummary, index: number): string {
  const id: Guid | undefined = (row as Partial<EntityMemberSummary>).id;
  return id ? id.value : `${SKELETON_ROW_PREFIX}${index}`;
}

/** MRT hands state changes as either a value or a function of the old one. */
function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

/**
 * What opening a member's row does: their profile, in a dialog over the roster. The row's click, the
 * name's link and the ⋯'s Open all come from here (#6722). A member with no individual has no profile
 * to open, so there is nothing, and the row opens nothing.
 */
export function memberProfileOpener(
  member: EntityMemberSummary,
  onViewProfile: (individualId: Guid) => void,
): (() => void) | undefined {
  // A loading placeholder reaches here as a row of nulls, with no individual on it.
  const individualId = (member as Partial<EntityMemberSummary>).individualId;
  if (!individualId) return undefined;
  return () => onViewProfile(individualId);
}

function MemberNameCell(
  props: Readonly<{
    member: EntityMemberSummary;
    onViewProfile: (individualId: Guid) => void;
    table: MRT_TableInstance<EntityMemberSummary>;
  }>,
) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  const { member, onViewProfile, table } = props;
  // A button rather than an anchor: the profile opens in a dialog over this page and there is no URL
  // to point at. A member with no individual gets plain text and the default avatar, because there is
  // no profile to open and no individual to read an image from.
  return (
    <NameCellContainer>
      <IndividualAvatar individual={member.individualId ? member.asIndividual() : undefined} />
      <CondensedCell
        title={
          <RowLink onClick={memberProfileOpener(member, onViewProfile)}>
            <SearchMatch table={table} text={member.fullName} />
          </RowLink>
        }
      />
    </NameCellContainer>
  );
}

/** Where a member's permissions are managed: their person page. */
export function memberPermissionsPath(member: EntityMemberSummary): string {
  return personPermissionsPath(member.userId);
}

/**
 * A member's row actions: the ⋯ that ends the row, with Open first where the member has a profile to
 * open (#6722), and their access actions, for a session that may manage them: icons while there is
 * room, in the ⋯ below `md`. It was an "Access" data column holding a kebab that was always folded, so a viewer who
 * cannot grant signing authority opened a menu to find one item in it, and the kebab sat centred in
 * a wide column under a header aligned to its left edge.
 *
 * Signing authority is offered only to a session that may grant it, and never on the viewer's own
 * row: the server refuses a grant to oneself, and Remove there would take away the viewer's own.
 */
function MemberAccessMenu(
  props: Readonly<{
    member: EntityMemberSummary;
    mayManageAccess: boolean;
    mayChangeSigningAuthority: boolean;
    isPending: boolean;
    /** Opens the member's profile; absent for a member with none. */
    onOpen?: () => void;
    onOpenPermissions: (member: EntityMemberSummary) => void;
    signingAuthority: Pick<MemberSigningAuthority, "grant" | "remove">;
  }>,
) {
  const { member, mayManageAccess, mayChangeSigningAuthority, isPending, onOpen, onOpenPermissions, signingAuthority } =
    props;
  const actions: RowAction[] = [];
  if (mayManageAccess) {
    actions.push({
      key: "open-permissions",
      label: "Open permissions",
      ariaLabel: `Open permissions for ${member.fullName}`,
      icon: <ManageAccountsIcon />,
      onClick: () => onOpenPermissions(member),
    });
  }
  if (mayManageAccess && mayChangeSigningAuthority) {
    // Grant and Remove share a key so they are one button that changes its icon and name: an Undo
    // returns focus to the button the change came from, and a button with a key of its own would have
    // been unmounted by the change it made.
    actions.push(
      member.hasSigningAuthority
        ? {
            key: "signing-authority",
            label: "Remove signing authority",
            ariaLabel: `Remove signing authority from ${member.fullName}`,
            icon: <EditOffIcon />,
            destructive: true,
            disabled: isPending,
            disabledReason: "Saving…",
            onClick: (menuButton) => signingAuthority.remove(member, menuButton),
          }
        : {
            key: "signing-authority",
            label: "Grant signing authority",
            ariaLabel: `Grant signing authority to ${member.fullName}`,
            icon: <DrawIcon />,
            disabled: isPending,
            disabledReason: "Saving…",
            onClick: (menuButton) => signingAuthority.grant(member, menuButton),
          },
    );
  }

  // A lone action stays an icon at every width: folded, it is a menu of one behind a kebab that says
  // nothing about what is in it, and the icon is no wider than the kebab.
  return (
    <RowActions
      actions={actions}
      ariaLabel={`Access for ${member.fullName}`}
      collapsed={actions.length === 1 ? false : undefined}
      open={onOpen ? { onClick: onOpen } : undefined}
    />
  );
}

/**
 * The legal entity's members, one page at a time. The search, the order and the page all travel
 * to `legal-entities/{legalEntityId}/members`: sorting or searching a fetched page would reorder
 * ten rows and present them as the roster.
 */
export default function EntityMembersTable(props: Readonly<EntityMembersTableInputProps>) {
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { canManageMemberAccess, inviteAction, renderTopToolbarCustomActions } = props;

  // State
  const [term, setTerm] = React.useState("");
  const [sorting, setSorting] = React.useState<MRT_SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = React.useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: LIST_PAGE_SIZE,
  });

  // Hooks
  const navigate = useNavigate();
  const session = useSession();
  const sessionUser = useSessionUser();
  const { openDialog } = useDialog();
  const signingAuthority = useMemberSigningAuthority(sessionUser?.id);
  const mayGrantSigningAuthority = sessionUser?.canGrantSigningAuthority() ?? false;
  const sessionUserId = sessionUser?.id;

  // Queries
  // Memoized on purpose, not defensively: the query is a class instance and reaches RTK Query's
  // arguments, so a fresh one on every render must not depend on the compiler to stay stable.
  const query = React.useMemo(() => {
    const order = entityMemberOrderFrom(sorting);
    return new EntityMemberQuery({
      paginationParameters: PaginationParameters.create(pagination),
      orderBy: order.orderBy,
      orderDir: order.orderDir,
      // The search box stops at this length; a pasted value is cut to it rather than refused.
      query: term.slice(0, ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH),
    });
  }, [pagination, sorting, term]);

  const membersQuery = useGetEntityMemberPageQuery({
    legalEntityId: session.user!.legalEntityId.value,
    query,
  });

  const members = React.useMemo(() => {
    if (!membersQuery.data) return PaginatedResponse.empty<EntityMemberSummary>();
    return PaginatedResponse.from<EntityMemberSummary>(
      EntityMemberSummary.fromJSONArray(membersQuery.data.data),
      membersQuery.data.headers,
    );
  }, [membersQuery.data]);

  const showFirstPage = () =>
    // A new result set is a new first page. Otherwise a reader who narrows the search lands on
    // page four of something whose page one they never saw.
    setPagination((current) => ({ ...current, pageIndex: 0 }));

  const handleViewProfile = useCallback(
    (individualId: Guid): void => {
      const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: <ViewIndividualProfile individualId={individualId} />,
        onClose: () => {
          navigate(parentPath.pathname);
        },
      });
    },
    [openDialog, navigate],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<EntityMemberSummary> }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={props.table} sx={searchFieldSpacing} />
              {/* No Column filters entry: no column filters, and the search covers name and email. */}
              <TableToolsMenu table={props.table} />
            </ToolbarButtons>
            {inviteAction}
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    [inviteAction, isMediumDisplaySize],
  );

  const columns = React.useMemo(
    () => [
      membersColumnHelper.accessor((member) => member.fullName, {
        header: "Name",
        id: NAME_COLUMN,
        enableSorting: true,
        meta: { kind: "name" },
        Cell: ({ row, table }) => (
          <MemberNameCell member={row.original} onViewProfile={handleViewProfile} table={table} />
        ),
      }),
      // Its own column where there is room, and under the name where there is not (#6722).
      membersColumnHelper.accessor((member) => member.email ?? "", {
        header: "Email",
        id: EMAIL_COLUMN,
        // The server sorts by name and by date joined, and by nothing else.
        enableSorting: false,
        meta: { kind: "text", fold: { into: NAME_COLUMN, priority: 1, render: (member) => member.email } },
        // The search matches the name and the email address, so both mark it (#6590).
        Cell: ({ row, table }) => <SearchMatch table={table} text={row.original.email} />,
      }),
      membersColumnHelper.display({
        header: "Role",
        id: ROLE_COLUMN,
        enableSorting: false,
        meta: { kind: "text" },
        Cell: ({ row }) => memberRole(row.original.hasSigningAuthority),
      }),
      membersColumnHelper.accessor((member) => formatDate(member.dateJoined) ?? "", {
        header: "Joined",
        id: DATE_JOINED_COLUMN,
        meta: { kind: "date" },
        Cell: ({ row }) => <DateCell value={row.original.dateJoined} />,
        // Not searched, so never marked as matching (#6590).
        enableGlobalFilter: false,
        enableSorting: true,
      }),
    ],
    [handleViewProfile],
  );

  const tableOptions: MRT_TableOptions<EntityMemberSummary> = {
    columns,
    data: members.data,
    enableRowSelection: false,
    enableTableHead: true,
    // Every row has its ⋯, with Open first, whether or not the session may manage access (#6722).
    enableRowActions: true,
    positionActionsColumn: "last",
    enableColumnPinning: true,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    // No column-level sizing or cell props for the actions column: the shared table shrinks it to its
    // buttons, one or two, at any width or pointer. Cell props set here would replace that rule rather
    // than add to it, and MRT's `size` does nothing in this semantic layout.
    renderRowActions: ({ row }) => (
      <MemberAccessMenu
        member={row.original}
        mayManageAccess={canManageMemberAccess}
        onOpen={memberProfileOpener(row.original, handleViewProfile)}
        mayChangeSigningAuthority={mayGrantSigningAuthority && !(sessionUserId?.isEqualTo(row.original.userId) ?? false)}
        isPending={signingAuthority.pendingUserIds.has(row.original.userId.value)}
        onOpenPermissions={(member) => navigate(memberPermissionsPath(member))}
        signingAuthority={signingAuthority}
      />
    ),
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "members", table, maxLength: ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: membersQuery.isError,
      // `isFetching`, not `isLoading`: every page, sort and keystroke empties the table while it is
      // in flight, and "nothing matched" then would be about rows already on their way.
      isLoading: membersQuery.isFetching,
      onRetry: () => void membersQuery.refetch(),
      subject: "members",
    }),
    getRowId: entityMemberRowId,
    muiTableBodyRowProps: ({ row }) => {
      const openProfile = memberProfileOpener(row.original, handleViewProfile);
      return openProfile ? { onClick: openProfile, sx: { cursor: "pointer" } } : {};
    },
    enableStickyHeader: true,
    enableStickyFooter: true,
    // No column can filter: the search reaches the server and covers name and email.
    enableColumnFilters: false,
    enableMultiSort: false,
    // The server sorts every answer, so there is no unsorted state for a third click to mean.
    enableSortingRemoval: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    // MRT's search field already waits 500ms after a keystroke before it sets the global filter
    // while `manualFiltering` is on, so this table adds no debounce of its own.
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setTerm(applyUpdater(updater, term) ?? "");
      showFirstPage();
    },
    onPaginationChange: (updater: MRT_Updater<MRT_PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages, so the reader goes back to the first.
      setPagination(next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 });
    },
    onSortingChange: (updater: MRT_Updater<MRT_SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      showFirstPage();
    },
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    renderToolbarInternalActions: renderToolbarInternalActions,
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTableContainerProps: {
      sx: {
        flex: 1,
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    muiToolbarAlertBannerProps: membersQuery.isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    rowCount: members.totalElements,
    state: {
      globalFilter: term,
      isLoading: membersQuery.isLoading,
      pagination,
      showAlertBanner: membersQuery.isError,
      showProgressBars: membersQuery.isFetching,
      sorting,
    },
  };

  return <MRTNoMemo<EntityMemberSummary> options={tableOptions} cards={{ defaultSorting: DEFAULT_SORTING }} />;
}
